<script setup>
import { computed } from 'vue';

const props = defineProps({
  customRoles: { type: Array, default: () => [] },
  hasError: { type: Boolean, default: false },
});

const role = defineModel('role', { type: String, default: 'agent' });
const customRoleId = defineModel('customRoleId', {
  type: Number,
  default: null,
});

const selectedCustomRole = computed(() =>
  props.customRoles.find(item => item.id === customRoleId.value)
);

// Perfil de acesso so vale para Agente: ao virar Administrador, some.
const onRoleChange = event => {
  const nextRole = event.target.value;
  role.value = nextRole;
  if (nextRole !== 'agent') customRoleId.value = null;
};

const onCustomRoleChange = event => {
  const value = event.target.value;
  customRoleId.value = value ? Number(value) : null;
};
</script>

<template>
  <div class="w-full">
    <label :class="{ error: hasError }">
      {{ $t('AGENT_MGMT.ROLE_FIELDS.ROLE.LABEL') }}
      <select
        :value="role"
        data-testid="agent-role-select"
        @change="onRoleChange"
      >
        <option value="administrator">
          {{ $t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') }}
        </option>
        <option value="agent">
          {{ $t('AGENT_MGMT.AGENT_TYPES.AGENT') }}
        </option>
      </select>
      <span v-if="hasError" class="message">
        {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
      </span>
    </label>
    <p
      class="mb-4 -mt-2 text-xs text-n-slate-11"
      data-testid="agent-role-help"
    >
      {{
        role === 'administrator'
          ? $t('AGENT_MGMT.ROLE_FIELDS.ROLE.ADMIN_HELP')
          : $t('AGENT_MGMT.ROLE_FIELDS.ROLE.AGENT_HELP')
      }}
    </p>
  </div>

  <div v-if="role === 'agent' && customRoles.length" class="w-full">
    <label>
      {{ $t('AGENT_MGMT.ROLE_FIELDS.CUSTOM_ROLE.LABEL') }}
      <select
        :value="customRoleId ?? ''"
        data-testid="agent-custom-role-select"
        @change="onCustomRoleChange"
      >
        <option value="">
          {{ $t('AGENT_MGMT.ROLE_FIELDS.CUSTOM_ROLE.NONE') }}
        </option>
        <option
          v-for="customRole in customRoles"
          :key="customRole.id"
          :value="customRole.id"
        >
          {{ customRole.name }}
        </option>
      </select>
    </label>
    <div
      v-if="selectedCustomRole"
      class="mb-4 -mt-2 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
      data-testid="agent-custom-role-summary"
    >
      <p class="mb-1 text-xs font-medium text-n-slate-12">
        {{ $t('AGENT_MGMT.ROLE_FIELDS.CUSTOM_ROLE.SUMMARY') }}
      </p>
      <ul class="mb-0 list-disc text-xs text-n-slate-11 ltr:pl-4 rtl:pr-4">
        <li
          v-for="permission in selectedCustomRole.permissions || []"
          :key="permission"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
          {{ $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`) }}
        </li>
      </ul>
    </div>
    <p v-else class="mb-4 -mt-2 text-xs text-n-slate-11">
      {{ $t('AGENT_MGMT.ROLE_FIELDS.CUSTOM_ROLE.HELP') }}
    </p>
  </div>
</template>
